import { onNuxtReady, useAsyncData, useRoute, useState } from '#imports'
import { type Ref, type ShallowReactive, computed } from 'vue'

import type { SearchResponse } from '@algolia/client-search'
import type { AlgoliaProduct } from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia'
import type { SearchConfiguration } from '@backmarket/http-api/src/api-specs-search-reco/search/searchConfiguration'
import { useAlgoliaSearchOptions } from '@backmarket/nuxt-layer-algolia/useAlgoliaSearchOptions'
import { useTestChallenge } from '@backmarket/nuxt-layer-algolia/useRedirectToTestChallenge'
import { useBuybackEnabled } from '@backmarket/nuxt-layer-buyback/app/composables/useBuybackEnabled/useBuybackEnabled'
import { buildUiFilters } from '@backmarket/nuxt-layer-recommendation/filtering/buildUiFilters'
import { filtersToWhereClause } from '@backmarket/nuxt-layer-recommendation/filtering/filtersToWhereClause'
import { getGraphFacet } from '@backmarket/nuxt-layer-recommendation/filtering/getGraphFacet'
import {
  algoliaHitToProduct,
  algoliaHitToVariant,
} from '@backmarket/nuxt-layer-recommendation/helpers.ts'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import algoliasearch from 'algoliasearch'

import { HITS_PER_PAGE, HITS_PER_PAGE_WITH_BUYBACK } from '../../constants'
import { ROUTES as PRODUCT_LIST_ROUTES } from '../../route-names'

import type {
  ProductsSearchData,
  SearchTrackingData,
} from './useProductsSearch.types'
import { buildSearchRequestOptions } from './useProductsSearch.utils'

const DEFAULT_DATA: ProductsSearchData = {
  products: [],
  total: 0,
  pageCount: 0,
  facets: [],
  priceFacet: undefined,
}

export function useProductsSearch(
  identifier: string,
  configuration: SearchConfiguration,
  searchParams: {
    query?: Ref<string>
    filtersInput: ShallowReactive<Record<string, string[]>>
    sortInput: Ref<string>
    pageInput: Ref<number>
    priceInput: Ref<[number, number] | undefined>
    disjunctiveFacetKeys?: Ref<string[]>
    userToken?: string
  },
  options?: { paused?: Ref<boolean> },
) {
  const searchTrackingData = useState<SearchTrackingData>(
    'search-query',
    () => ({
      id: '',
      index: '',
    }),
  )
  const algoliaSearchOption = useAlgoliaSearchOptions('search')
  const algoliaClient = algoliasearch('dummy_app_id', 'dummy_key', {
    ...algoliaSearchOption,
  })
  const logger = useLogger()
  const { redirectToTestChallengePage, isTestChallengeError } =
    useTestChallenge()
  const { isBuybackEnabled } = useBuybackEnabled()

  const route = useRoute()
  const isSearchResultsPage = route.name === PRODUCT_LIST_ROUTES.SEARCH_RESULTS

  // Search Configuration
  const index = computed(() => {
    return (
      [
        configuration.indexes.active,
        ...(configuration.indexes?.other ?? []),
      ].find(({ name }) => name === searchParams.sortInput.value) ??
      configuration.indexes.active
    )
  })

  const requestOptions = computed(() =>
    buildSearchRequestOptions({
      configuration,
      indexQueryParams: index.value.queryParams,
      filtersInput: searchParams.filtersInput,
      priceInput: searchParams.priceInput.value,
      pageInput: searchParams.pageInput.value,
      hitsPerPage: isBuybackEnabled
        ? HITS_PER_PAGE_WITH_BUYBACK
        : HITS_PER_PAGE,
    }),
  )
  const experiments = useExperiments()
  const withFreshDataRefresh =
    experiments['experiment.plpDuplicatedAlgoliaCalls'] ===
    'withDuplicatedCalls'
  const withVariantLinks =
    experiments['experiment.algoliaCardLinks'] === 'withVariantLinks'

  const asyncData = useAsyncData(
    getAsyncDataKey('products-search', {
      identifier,
      index: index.value.name,
    }),
    async () => {
      if (options?.paused?.value) return null

      try {
        const queryValue = searchParams.query?.value ?? ''

        const disjunctiveKeys = (
          searchParams.disjunctiveFacetKeys?.value ?? []
        ).filter((key) => (searchParams.filtersInput[key]?.length ?? 0) > 0)

        const { results } = await algoliaClient.multipleQueries<AlgoliaProduct>(
          [
            {
              indexName: index.value.name,
              query: queryValue,
              params: {
                ...requestOptions.value,
                ...(searchParams.userToken && {
                  userToken: searchParams.userToken,
                }),
              },
            },
            ...disjunctiveKeys.map((key) => {
              const { [key]: _excluded, ...filtersWithoutKey } =
                searchParams.filtersInput

              return {
                indexName: index.value.name,
                query: queryValue,
                params: {
                  ...requestOptions.value,
                  filters: filtersToWhereClause(
                    filtersWithoutKey,
                    configuration.complexFilter,
                  ),
                  facets: [key],
                  hitsPerPage: 0,
                  page: 0,
                },
              }
            }),
          ],
        )
        const [result, ...facetResults] =
          results as SearchResponse<AlgoliaProduct>[]

        if (disjunctiveKeys.length > 0) {
          result.facets = {
            ...result.facets,
            ...Object.fromEntries(
              disjunctiveKeys.map((key, i) => [
                key,
                facetResults[i].facets?.[key] ?? {},
              ]),
            ),
          }
        }

        logger.info('[S&R] PLP search query success', {
          endpointName: 'algoliaSearch',
          type: 'API_REQUEST_SUCCESS',
          searchProcessingTime: result.processingTimeMS,
          owners: ['bot-squad-search-and-recommendation-front'],
        })

        if (result.nbHits === 0) {
          logger.info('[S&R] PLP returned empty results', {
            type: 'EMPTY_SEARCH_RESULTS',
            owners: ['bot-squad-search-and-recommendation-front'],
          })
        }

        searchTrackingData.value = {
          id: result.queryID,
          index: index.value.name,
        }

        return result
      } catch (e) {
        logger.error('[S&R] PLP search query failed', {
          endpointName: 'algoliaSearch',
          type: 'API_REQUEST_FAIL',
          e,
          owners: ['bot-squad-search-and-recommendation-front'],
        })

        if (isTestChallengeError(e)) {
          redirectToTestChallengePage()
        }

        throw e
      }
    },
    {
      default: () => DEFAULT_DATA,
      transform: (result) => {
        if (!result) return DEFAULT_DATA

        const useGradeLink =
          withVariantLinks &&
          (isSearchResultsPage ||
            hasUnsupportedFilterAttribute(
              searchParams.filtersInput,
              configuration.complexFilter,
              requestOptions.value.numericFilters,
            ))

        const pageOffset =
          (searchParams.pageInput.value - 1) * result.hitsPerPage

        return {
          total: result.nbHits,
          pageCount: result.nbPages,
          products:
            configuration.productGroupingType === 'parent'
              ? result.hits.map((hit, i) => ({
                  ...algoliaHitToProduct(hit, { useGradeLink }),
                  position: pageOffset + i + 1,
                }))
              : result.hits.map((hit, i) => ({
                  ...algoliaHitToVariant(hit),
                  position: pageOffset + i + 1,
                })),
          facets: buildUiFilters([], configuration.facets, result.facets),
          priceFacet:
            configuration.priceFacet &&
            result.facets &&
            getGraphFacet(configuration.priceFacet, result.facets, {
              avg: result.facets_stats?.price.avg ?? 0,
              max: result.facets_stats?.price.max ?? 0,
            }),
        }
      },
      lazy: false,
      watch: [() => index.value.name, requestOptions],
    },
  )

  if (import.meta.client && withFreshDataRefresh) {
    const { refresh } = asyncData
    onNuxtReady(() => {
      refresh()
    })
  }

  return asyncData
}

const PARENT_GROUPING_ATTRIBUTES = new Set([
  'cat_id',
  'brand',
  'brand_clean',
  'model',
  'model_clean',
])

function hasUnsupportedFilterAttribute(
  filtersInput: Record<string, string[]>,
  complexFilter = '',
  numericFilters: string[] = [],
) {
  const hasUnsupportedFacet = Object.entries(filtersInput).some(
    ([attribute, values]) =>
      values.length > 0 && !PARENT_GROUPING_ATTRIBUTES.has(attribute),
  )

  if (hasUnsupportedFacet) return true

  const filterExpressions = [complexFilter, ...numericFilters]

  return filterExpressions.some((filter) => {
    // A full Algolia filter parser would be more precise but harder to read.
    // PLP filters follow this grammar, so collecting field prefixes is enough.
    return Array.from(
      filter.matchAll(
        /(?:^|\b(?:AND|OR|NOT)\b|\()\s*([A-Za-z_][\w.-]*)\s*(?=:|<=|>=|!=|=|<|>)/g,
      ),
      (match) => match[1],
    ).some((attribute) => !PARENT_GROUPING_ATTRIBUTES.has(attribute))
  })
}
